/**
 * FAIR reports the site's ERP holds for an order line's jobs.
 *
 * These are files someone uploaded in the ERP, copied to the portal as they
 * are, unlike the certificate and despatch note, which are generated. Rendered
 * inside the line's Documents tab, so the same feature switch applies.
 */
import { useState } from 'react';
import { erpDocumentsAPI, ERPDocument } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useFetchData } from '../../hooks/useFetchData';
import { formatFileSize } from './PODetailHelpers';
import { Button } from '../ui/button';
import { ClipboardCheck, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** Types opened in a tab rather than downloaded; matches the backend's inline list. */
const INLINE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

interface ERPDocumentListProps {
  /** PO line whose jobs the files belong to. */
  lineId: number;
}

/**
 * The line's FAIR reports with a button to open each.
 *
 * @param lineId - the PO line whose jobs the files belong to
 * @returns the list, a loading line, an error line, or a short "none" note
 */
export default function ERPDocumentList({ lineId }: ERPDocumentListProps) {
  const { formatDate } = useFormatters();
  const [opening, setOpening] = useState<number | null>(null);
  const { data, loading, error, refetch } = useFetchData<ERPDocument[]>(
    async () => (await erpDocumentsAPI.listForLine(lineId)).data,
    { deps: [lineId] },
  );

  // Same anchor approach as the generated documents: window.open after an
  // await is swallowed by popup blockers.
  const open = async (doc: ERPDocument) => {
    setOpening(doc.id);
    try {
      const response = await erpDocumentsAPI.getFile(lineId, doc.id);
      const type = String(response.headers['content-type'] || 'application/octet-stream');
      const url = window.URL.createObjectURL(new Blob([response.data], { type }));
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      // Only PDFs and PNG/JPEG images open in a tab (the backend serves only
      // those inline); anything else is saved under its real name.
      if (!INLINE_TYPES.some((t) => type.startsWith(t))) link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err: unknown) {
      console.error('Failed to open ERP document:', err);
      toast.error(`Could not open ${doc.file_name}`);
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className="border-t pt-4 space-y-2">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <div className="text-sm font-medium">FAIR reports</div>
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Loading FAIR reports…
        </div>
      )}
      {!loading && error && (
        <div className="flex items-center gap-3">
          <p className="text-xs text-red-600">Could not load the FAIR reports.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
        </div>
      )}
      {!loading && !error && (data?.length ?? 0) === 0 && (
        <p className="text-xs text-muted-foreground">No FAIR report from the ERP for this line&apos;s jobs.</p>
      )}
      {!loading && !error && (data ?? []).map((doc) => (
        <div key={doc.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{doc.file_name}</div>
            <div className="text-xs text-muted-foreground">
              {[doc.fair_number && `FAIR ${doc.fair_number}`, doc.job_number && `Job ${doc.job_number}`,
                doc.erp_created_at && `uploaded ${formatDate(doc.erp_created_at)}`, formatFileSize(doc.size)]
                .filter(Boolean).join(' · ')}
            </div>
          </div>
          <Button
            type="button" variant="outline" size="sm" disabled={opening !== null}
            aria-label={`View ${doc.file_name}`} onClick={() => open(doc)}
          >
            {opening === doc.id
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden="true" />
              : <ExternalLink className="h-3.5 w-3.5 mr-1" aria-hidden="true" />}
            {opening === doc.id ? 'Opening…' : 'View FAIR report'}
          </Button>
        </div>
      ))}
    </div>
  );
}
